import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import apiClient from '../../utils/apiClient';
import paths from '../../routes/paths';

const useAccountRecovery = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState(() => {
    // Get email from sessionStorage if available
    return sessionStorage.getItem('recoveryEmail') || '';
  });
  const [verificationCode, setVerificationCode] = useState(() => {
    // Get verification code from sessionStorage if available
    return sessionStorage.getItem('recoveryCode') || '';
  });

  // Save email to sessionStorage whenever it changes
  useEffect(() => {
    if (email) {
      sessionStorage.setItem('recoveryEmail', email);
    }
  }, [email]);

  // Save verification code to sessionStorage whenever it changes
  useEffect(() => {
    if (verificationCode) {
      sessionStorage.setItem('recoveryCode', verificationCode);
    }
  }, [verificationCode]);

  // Clear recovery data when password is successfully reset
  const clearRecoveryData = () => {
    sessionStorage.removeItem('recoveryEmail');
    sessionStorage.removeItem('recoveryCode');
    setEmail('');
    setVerificationCode('');
  };

  // Step 1: Request password reset (send verification code to email)
  const requestPasswordReset = async (emailAddress) => {
    setIsLoading(true);
    try {
      const response = await apiClient.post('/auth/forgot-password', {
        email: emailAddress
      });
      
      if (response.data) {
        setEmail(emailAddress);
        toast.success('Verification code sent to your email', { theme: 'colored' });
        navigate(paths.simpleVerificationCode, { state: { email: emailAddress } });
        return { success: true };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to send verification code';
      toast.error(errorMessage, { theme: 'colored' });
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Verify the code sent to email
  const verifyCode = async (code) => {
    setIsLoading(true);
    try {
      const response = await apiClient.post('/auth/verify-reset-code', {
        email: email,
        code: code
      });
      
      if (response.data) {
        setVerificationCode(code);
        toast.success('Code verified successfully', { theme: 'colored' });
        navigate(paths.simpleResetPassword, { state: { email: email } });
        return { success: true };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Invalid verification code';
      toast.error(errorMessage, { theme: 'colored' });
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  // Step 3: Reset password with new password
  const resetPassword = async (newPassword, confirmPassword) => {
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match', { theme: 'colored' });
      return { success: false, error: 'Passwords do not match' };
    }

    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters', { theme: 'colored' });
      return { success: false, error: 'Password must be at least 6 characters' };
    }

    setIsLoading(true);
    try {
      const response = await apiClient.post('/auth/reset-password', {
        email: email,
        code: verificationCode,
        newPassword: newPassword
      });
      
      if (response.data) {
        clearRecoveryData();
        navigate(paths.simpleAccountRecoverySuccess);
        return { success: true };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to reset password';
      toast.error(errorMessage, { theme: 'colored' });
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    email,
    isLoading,
    requestPasswordReset,
    verifyCode,
    resetPassword
  };
};

export default useAccountRecovery;

